import {
  IconBanknote,
  IconBuildings,
  IconMagnifyingGlass,
  Primary12SemiBold,
  Box
} from '@sequencehq/core-components'
import { NodeTimeline, useNodeTimelineVariant } from 'Automations/components'
import {
  ExecutionSummaryComponent,
  ExecutionSummaryComponentProps
} from 'Automations/nodeDefinitions/types'
import {
  CancelledItem,
  FailedItem,
  hasCancelledTask,
  hasFailedTask
} from 'Automations/nodeDefinitions/executionSummaryUtils'
import { useMemo } from 'react'
import { ExtractedBillingScheduleDataTimelineItem } from './ExtractedBillingScheduleDataTimelineItem'
import { ExtractedCustomerDataTimelineItem } from './ExtractedCustomerDataTimelineItem'
import { ExtractedBillingSchedule, ExtractedCustomer } from './types'
import { TASK_EXECUTORS } from 'Automations/automations.constants'

type ExtractedData = {
  customer?: ExtractedCustomer
  billingSchedule?: ExtractedBillingSchedule
}

export const ContractExtractionExecutionSummary: ExecutionSummaryComponent = ({
  tasks,
  automationExecution,
  isLatestNodeExecution
}) => {
  const showPreviews = useNodeTimelineVariant() === 'DEFAULT'
  const {
    allDataRetrieved,
    taskFailed,
    taskCancelled,
    extractedCustomer,
    extractedBillingSchedule,
    extractionTask
  } = useContractExtractionData(tasks)

  if (taskFailed) {
    return (
      <FailedItem
        label="Contract extraction failed"
        timestamp={extractionTask?.startTime}
        isLatestNodeExecution={isLatestNodeExecution}
      />
    )
  }

  if (taskCancelled) {
    return (
      <CancelledItem
        label="Contract extraction cancelled"
        timestamp={extractionTask?.startTime}
        isLatestNodeExecution={isLatestNodeExecution}
      />
    )
  }

  if (!allDataRetrieved) {
    return (
      <ExtractionInProgressTimelineItem
        isLatestNodeExecution={isLatestNodeExecution ?? false}
        automationExecution={automationExecution}
      />
    )
  }

  return (
    <>
      {extractedCustomer ? (
        <ExtractedCustomerDataTimelineItem
          customer={extractedCustomer}
          timestamp={extractionTask?.startTime}
          showPreview={showPreviews}
        />
      ) : (
        <NoCustomerDataTimelineItem timestamp={extractionTask?.startTime} />
      )}
      {extractedBillingSchedule ? (
        <ExtractedBillingScheduleDataTimelineItem
          billingSchedule={extractedBillingSchedule}
          isLatestNodeExecution={isLatestNodeExecution ?? false}
          timestamp={extractionTask?.startTime}
          fileName={automationExecution.input.fileName ?? 'Unknown file'}
          customer={extractedCustomer}
          showPreview={showPreviews}
        />
      ) : (
        <NoPricingDataTimelineItem
          isLatestNodeExecution={isLatestNodeExecution ?? false}
          timestamp={extractionTask?.startTime}
        />
      )}
    </>
  )
}

const ExtractionInProgressTimelineItem = ({
  isLatestNodeExecution,
  automationExecution
}: {
  isLatestNodeExecution: boolean
  automationExecution: ExecutionSummaryComponentProps['automationExecution']
}) => {
  return (
    <NodeTimeline.Item
      icon={<IconMagnifyingGlass />}
      showStem={!isLatestNodeExecution}
    >
      <NodeTimeline.Title>
        <NodeTimeline.UserAction
          label={
            <>
              <Box as="span" fontWeight={Primary12SemiBold.fontWeight}>
                Contract intake agent
              </Box>{' '}
              is looking for contract details in the uploaded document
            </>
          }
          timestamp={automationExecution.createTime}
        />
      </NodeTimeline.Title>
    </NodeTimeline.Item>
  )
}

const NoCustomerDataTimelineItem = ({ timestamp }: { timestamp?: string }) => {
  return (
    <NodeTimeline.Item icon={<IconBuildings />} showStem>
      <NodeTimeline.Title>
        <NodeTimeline.UserAction
          label="We couldn't extract any customer information from the document."
          timestamp={timestamp}
        />
      </NodeTimeline.Title>
    </NodeTimeline.Item>
  )
}

const NoPricingDataTimelineItem = ({
  isLatestNodeExecution,
  timestamp
}: {
  isLatestNodeExecution: boolean
  timestamp?: string
}) => {
  return (
    <NodeTimeline.Item
      icon={<IconBanknote />}
      showStem={!isLatestNodeExecution}
    >
      <NodeTimeline.Title>
        <NodeTimeline.UserAction
          label="We couldn't extract any pricing information from the document."
          timestamp={timestamp}
        />
      </NodeTimeline.Title>
    </NodeTimeline.Item>
  )
}

const useContractExtractionData = (
  tasks: ExecutionSummaryComponentProps['tasks']
) => {
  const extractionTask = useMemo(() => {
    return tasks.find(
      task =>
        task.inputData.taskExecutorConfiguration?.executor ===
        TASK_EXECUTORS.extractContract
    )
  }, [tasks])

  const allDataRetrieved = useMemo(() => {
    return tasks.every(task => task.status === 'COMPLETED')
  }, [tasks])

  const taskFailed = useMemo(() => hasFailedTask(tasks), [tasks])

  const taskCancelled = useMemo(() => hasCancelledTask(tasks), [tasks])

  const extractedCustomer = useMemo(() => {
    return (extractionTask?.outputData.extractedData as ExtractedData)
      ?.customer as ExtractedCustomer
  }, [extractionTask])

  const extractedBillingSchedule = useMemo(() => {
    return (extractionTask?.outputData.extractedData as ExtractedData)
      ?.billingSchedule as ExtractedBillingSchedule
  }, [extractionTask])

  return {
    allDataRetrieved,
    taskFailed,
    taskCancelled,
    extractedCustomer,
    extractedBillingSchedule,
    extractionTask
  }
}
